import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Save, Plus, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { apiClient } from '../api/client';

function Settings({ onConfigured }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isAddMode = searchParams.get('mode') === 'new';
  
  const [config, setConfig] = useState({
    apiEndpoint: 'https://qs.elviroa.com/api',
    appId: '',
    apiKey: '',
  });
  const [isNewApp, setIsNewApp] = useState(false);
  const [appName, setAppName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    // 如果是添加模式，清空配置，只保留 endpoint
    if (isAddMode) {
      const storedConfig = localStorage.getItem('qianshu_config');
      if (storedConfig) {
        const parsedConfig = JSON.parse(storedConfig);
        setConfig({
          apiEndpoint: parsedConfig.apiEndpoint,
          appId: '',
          apiKey: '',
        });
      }
      setAppName('');
      return;
    }
    
    // 编辑模式：加载当前应用配置
    const storedConfig = localStorage.getItem('qianshu_config');
    if (storedConfig) {
      const parsedConfig = JSON.parse(storedConfig);
      setConfig(parsedConfig);
      
      // 从应用列表中加载应用名称
      const storedApps = localStorage.getItem('qianshu_apps');
      if (storedApps) {
        const apps = JSON.parse(storedApps);
        const currentApp = apps.find(app => app.appId === parsedConfig.appId);
        if (currentApp) {
          setAppName(currentApp.name);
        }
      }
    }
  }, [isAddMode]);

  const handleSave = () => {
    if (!config.apiEndpoint || !config.appId || !config.apiKey) {
      setError('请填写所有必填字段');
      return;
    }

    // 保存当前配置
    localStorage.setItem('qianshu_config', JSON.stringify(config));
    
    // 添加到应用列表
    const storedApps = localStorage.getItem('qianshu_apps');
    const apps = storedApps ? JSON.parse(storedApps) : [];
    const existingIndex = apps.findIndex(app => app.appId === config.appId);
    
    const defaultName = `应用 ${config.appId.substring(4, 12)}`;
    const appInfo = {
      name: appName.trim() || defaultName,
      appId: config.appId,
      apiKey: config.apiKey,
      apiEndpoint: config.apiEndpoint,
    };
    
    if (existingIndex >= 0) {
      apps[existingIndex] = appInfo;
    } else {
      apps.push(appInfo);
    }
    
    localStorage.setItem('qianshu_apps', JSON.stringify(apps));
    
    setSuccess('配置已保存');
    setTimeout(() => {
      setSuccess(null);
      if (isAddMode) {
        navigate('/');
      } else {
        onConfigured();
      }
    }, 1500);
  };

  const handleRegisterApp = async () => {
    if (!appName) {
      setError('请输入应用名称');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 临时设置 API 端点用于注册
      const tempConfig = {
        apiEndpoint: config.apiEndpoint,
        appId: '',
        apiKey: '',
      };
      localStorage.setItem('qianshu_config', JSON.stringify(tempConfig));

      const data = await apiClient.registerApp(appName);
      
      const newConfig = {
        apiEndpoint: config.apiEndpoint,
        appId: data.data.app_id,
        apiKey: data.data.api_key,
      };

      setConfig(newConfig);
      localStorage.setItem('qianshu_config', JSON.stringify(newConfig));
      
      // 添加到应用列表
      const storedApps = localStorage.getItem('qianshu_apps');
      const apps = storedApps ? JSON.parse(storedApps) : [];
      apps.push({
        name: appName,
        appId: data.data.app_id,
        apiKey: data.data.api_key,
        apiEndpoint: config.apiEndpoint,
      });
      localStorage.setItem('qianshu_apps', JSON.stringify(apps));
      
      setSuccess('应用注册成功！配置已自动保存。');
      setIsNewApp(false);
      
      setTimeout(() => {
        setSuccess(null);
        if (isAddMode) {
          navigate('/');
        } else {
          onConfigured();
        }
      }, 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {isAddMode && (
          <button
            onClick={() => navigate('/')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回首页
          </button>
        )}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isAddMode ? '添加新应用' : '乾枢管理后台'}
          </h1>
          <p className="text-gray-600">
            {isAddMode ? '配置新应用的连接信息' : '配置您的 API 连接信息'}
          </p>
        </div>

        {error && (
          <div className="card bg-red-50 border-red-200 mb-6">
            <div className="flex items-center text-red-800">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="card bg-green-50 border-green-200 mb-6">
            <div className="flex items-center text-green-800">
              <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>{success}</span>
            </div>
          </div>
        )}

        <div className="card">
          <div className="mb-6">
            <label className="label">API 端点</label>
            <input
              type="text"
              className="input"
              placeholder="https://your-worker.workers.dev"
              value={config.apiEndpoint}
              onChange={(e) => setConfig({ ...config, apiEndpoint: e.target.value })}
            />
            <p className="mt-1 text-sm text-gray-500">
              您的 Cloudflare Workers API 地址
            </p>
          </div>

          {!isNewApp ? (
            <>
              <div className="mb-6">
                <label className="label">应用名称</label>
                <input
                  type="text"
                  className="input"
                  placeholder="我的 Unity 游戏"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                />
                <p className="mt-1 text-sm text-gray-500">
                  为您的应用起一个便于识别的名称（可选）
                </p>
              </div>

              <div className="mb-6">
                <label className="label">应用 ID</label>
                <input
                  type="text"
                  className="input"
                  placeholder="app_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={config.appId}
                  onChange={(e) => setConfig({ ...config, appId: e.target.value })}
                />
              </div>

              <div className="mb-6">
                <label className="label">API Key</label>
                <input
                  type="password"
                  className="input"
                  placeholder="qsk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={config.apiKey}
                  onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                />
              </div>

              <div className="flex justify-between items-center">
                <button
                  onClick={() => setIsNewApp(true)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  还没有应用？点击注册
                </button>
                <button
                  onClick={handleSave}
                  className="btn-primary flex items-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  保存配置
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-6">
                <label className="label">应用名称</label>
                <input
                  type="text"
                  className="input"
                  placeholder="我的 Unity 游戏"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                />
                <p className="mt-1 text-sm text-gray-500">
                  为您的应用起一个便于识别的名称
                </p>
              </div>

              <div className="flex justify-between items-center">
                <button
                  onClick={() => setIsNewApp(false)}
                  className="text-gray-600 hover:text-gray-700 text-sm font-medium"
                >
                  ← 返回
                </button>
                <button
                  onClick={handleRegisterApp}
                  className="btn-primary flex items-center"
                  disabled={loading}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {loading ? '注册中...' : '注册应用'}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 card bg-blue-50 border-blue-200">
          <h3 className="font-semibold text-blue-900 mb-2">📖 使用说明</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>1. 确保您的 Cloudflare Workers API 已部署并运行</li>
            <li>2. 如果是首次使用，点击"注册应用"创建新应用</li>
            <li>3. 如果已有应用，直接填写 App ID 和 API Key</li>
            <li>4. 保存配置后即可使用管理后台</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Settings;
